import React, { useState, useMemo, useCallback } from 'react';
import { Skeleton } from "@/components/ui/skeleton";
import { FileQuestion, Users, Building2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { DataTable, type DataTableColumn } from "@/components/ui/DataTable";
import { useDutyRosterManagement, useBulkEmployeeGroupAssignment } from "@/hooks/useDutyRosterManagement";
import { useDutyRosterEmployeesWithGroups } from "@/hooks/useQueryHooks";
import { useAuth } from "@/contexts/auth";
import { toast } from "sonner";
import { normalizeResidency } from '@/utils/residencyUtils';
import { getStatusColor, StatusBadge } from '@/utils/statusUtils';

// Import Employee type from useEmployeeManagement
type Employee = {
  id: string;
  name: string;
  email: string;
  role: string;
  department: string;
  position: string;
  status: string;
  shift: string;
  residency: string;
  workerType?: string;
  employee_id?: string;
  is_duty_roster?: boolean;
  group_id?: string;
  group_name?: string;
};

interface DutyRosterEmployeesListProps {
  users: Employee[];
  isLoading: boolean;
  onViewProfile: (employee: Employee) => void;
  onEdit: (employee: Employee) => void;
  onViewOvertimeHistory: (employee: Employee) => void;
  onChangeStatus: (employee: Employee) => void;
}

export function DutyRosterEmployeesList({
  isLoading
}: DutyRosterEmployeesListProps) {
  const [selectedEmployees, setSelectedEmployees] = useState<Set<string>>(new Set());
  const [isAssignDialogOpen, setIsAssignDialogOpen] = useState(false);
  const [selectedGroupId, setSelectedGroupId] = useState<string>('');
  
  // Filters for department and group
  const [deptFilter, setDeptFilter] = useState<string>('');
  const [groupFilter, setGroupFilter] = useState<string[]>([]);
  const [groupFilterOpen, setGroupFilterOpen] = useState(false);
  const [deptFilterOpen, setDeptFilterOpen] = useState(false);
  
  const selectedEmployeeIds = useMemo(() => Array.from(selectedEmployees), [selectedEmployees]);
  
  const { user } = useAuth();
  
  const { 
    groups: dutyRosterGroups, 
    groupsLoading: isLoadingGroups
  } = useDutyRosterManagement(user?.companyId as string || '');
  
  // ✅ NEW: Fetch duty roster employees with group information
  const { 
    data: dutyRosterEmployeesWithGroups = [], 
    isLoading: isLoadingDutyRosterEmployees 
  } = useDutyRosterEmployeesWithGroups(user?.companyId as string || '');
  
  const {
    bulkAssign,
    isBulkAssigning
  } = useBulkEmployeeGroupAssignment(user?.companyId as string || '');

  // ✅ UPDATED: Extract employees array from RPC response
  const dutyRosterEmployees = React.useMemo(() => {
    if (!dutyRosterEmployeesWithGroups || typeof dutyRosterEmployeesWithGroups !== 'object') {
      return [];
    }
    
    // The RPC function returns a JSONB object with employees array
    if (Array.isArray(dutyRosterEmployeesWithGroups)) {
      return dutyRosterEmployeesWithGroups;
    }
    
    // If it's an object with employees property
    if (dutyRosterEmployeesWithGroups.employees && Array.isArray(dutyRosterEmployeesWithGroups.employees)) {
      return dutyRosterEmployeesWithGroups.employees;
    }
    
    return [];
  }, [dutyRosterEmployeesWithGroups]);

  // Get unique residency types from selected employees
  const selectedEmployeeResidencies = selectedEmployees.size > 0 
    ? [...new Set(Array.from(selectedEmployees).map(empId => {
        const employee = dutyRosterEmployees.find(emp => emp.id === empId);
        return employee?.residency;
      }).filter(Boolean))]
    : [];

  // Filter groups by residency type of selected employees
  const getFilteredGroups = () => {
    if (selectedEmployeeResidencies.length === 0) return dutyRosterGroups || [];
    
    // Normalize employee residencies to duty roster format
    const normalizedEmployeeResidencies = selectedEmployeeResidencies.map(normalizeResidency);
    
    // If employees have different residencies, return empty array (no groups available)
    if (normalizedEmployeeResidencies.length > 1) {
      return [];
    }
    
    // If all selected employees have the same residency, filter groups by that residency
    // Normalize both employee and group residencies for comparison
    return dutyRosterGroups?.filter(group => {
      const normalizedGroupResidency = normalizeResidency(group.residency_type);
      const normalizedEmployeeResidency = normalizedEmployeeResidencies[0];
      return normalizedGroupResidency === normalizedEmployeeResidency;
    }) || [];
  };

  // Build table rows from duty roster employees with filters
  const tableRows = useMemo(() => {
    return dutyRosterEmployees
      .filter(e => !deptFilter || (e.department || '') === deptFilter)
      .filter(e => {
        if (groupFilter.length === 0) return true;
        return e.group_id && groupFilter.includes(e.group_id);
      })
      .map(e => ({
        id: e.id,
        name: e.name,
        employee_id: e.employee_id || '',
        department: e.department || 'Unassigned',
        position: e.position || '',
        status: e.status,
        residency: e.residency || '',
        group_name: e.group_name || '',
        group_id: e.group_id || ''
      }));
  }, [dutyRosterEmployees, deptFilter, groupFilter]);

  const allFilteredSelected = useMemo(() => {
    if (tableRows.length === 0) return false;
    return tableRows.every(r => selectedEmployees.has(r.id));
  }, [tableRows, selectedEmployees]);

  const toggleSelectAllFiltered = useCallback((checked: boolean) => {
    // Toggle selection for all filtered rows
    tableRows.forEach(r => {
      const isSelected = selectedEmployees.has(r.id);
      if (checked && !isSelected) {
        setSelectedEmployees(prev => new Set([...prev, r.id]));
      }
      if (!checked && isSelected) {
        setSelectedEmployees(prev => {
          const newSet = new Set(prev);
          newSet.delete(r.id);
          return newSet;
        });
      }
    });
  }, [tableRows, selectedEmployees]);

  const toggleEmployeeSelection = useCallback((employeeId: string) => {
    setSelectedEmployees(prev => {
      const newSet = new Set(prev);
      if (newSet.has(employeeId)) {
        newSet.delete(employeeId);
      } else {
        newSet.add(employeeId);
      }
      return newSet;
    });
  }, []);

  // Handler for individual employee group assignment
  const handleIndividualEmployeeAssignment = React.useCallback((employeeId: string) => {
    // Clear other selections
    selectedEmployees.forEach(id => {
      if (id !== employeeId) {
        toggleEmployeeSelection(id);
      }
    });
    
    // Select the target employee if not already selected
    if (!selectedEmployees.has(employeeId)) {
      toggleEmployeeSelection(employeeId);
    }
    
    // Open modal after ensuring selection
    const checkAndOpenModal = () => {
      if (selectedEmployees.has(employeeId) || selectedEmployeeIds.length > 0) {
        setIsAssignDialogOpen(true);
      } else {
        setTimeout(checkAndOpenModal, 20);
      }
    };
    
    setTimeout(checkAndOpenModal, 10);
  }, [selectedEmployees, selectedEmployeeIds, toggleEmployeeSelection]);

  const handleAssignGroup = async () => {
    if (selectedEmployees.size === 0 || !selectedGroupId) {
      toast.error("Please select employees and a group");
      return;
    }

    try {
      await bulkAssign({
        employeeIds: Array.from(selectedEmployees),
        groupId: selectedGroupId,
        assignedBy: user?.id
      });
      
      setSelectedEmployees(new Set());
      setSelectedGroupId('');
      setIsAssignDialogOpen(false);
    } catch (error) {
      console.error('Error assigning employees to group:', error);
      toast.error("Failed to assign employees to group");
    }
  };

  // Define columns matching Shifts tab pattern
  type Row = typeof tableRows[number];
  const columns: DataTableColumn<Row>[] = useMemo(() => [
    {
      key: 'id',
      header: (
        <Checkbox
          checked={allFilteredSelected}
          onCheckedChange={(v) => toggleSelectAllFiltered(!!v)}
          aria-label="Select all filtered"
        />
      ),
      sortable: false,
      searchable: false,
      className: 'w-10',
      hidden: false,
      hideFromDropdown: true,
      render: (_v, row) => (
        <Checkbox
          checked={selectedEmployees.has(row.id)}
          onCheckedChange={() => toggleEmployeeSelection(row.id)}
          aria-label="Select row"
        />
      )
    },
    {
      key: 'name',
      header: 'Employee Name',
      sortable: true,
      searchable: true,
      className: 'text-left',
      render: (value, row) => {
        const getInitials = (name: string): string => {
          if (!name) return '?';
          const names = name.trim().split(' ');
          if (names.length === 1) return names[0].charAt(0).toUpperCase();
          return (names[0].charAt(0) + names[names.length - 1].charAt(0)).toUpperCase();
        };

        return (
          <div className="flex items-center space-x-3">
            <Avatar className="h-10 w-10 flex-shrink-0 ring-2 ring-background border-2 border-border">
              <AvatarFallback className="bg-gradient-to-br from-blue-100 to-blue-200 text-blue-700 font-semibold">
                {getInitials(String(value))}
              </AvatarFallback>
            </Avatar>
            <div className="flex flex-col min-w-0">
              <span className="font-semibold text-foreground truncate">{String(value)}</span>
              <span className="text-sm text-muted-foreground truncate">
                ID: {String(row.employee_id)}
              </span>
            </div>
          </div>
        );
      },
      exportRender: (value) => String(value)
    },
    {
      key: 'employee_id',
      header: 'Employee ID',
      sortable: true,
      searchable: true,
      hidden: true, // Hide column from UI but keep it searchable
      render: (value) => String(value || ''),
      exportRender: (value) => String(value || '')
    },
    {
      key: 'department',
      header: 'Department',
      sortable: true,
      searchable: true,
      render: (value) => (
        <div className="text-center">
          <Badge variant="outline" className={`whitespace-normal ${getStatusColor('neutral', 'light')}`}>
            <Building2 className="h-3 w-3 mr-1" />
            {String(value)}
          </Badge>
        </div>
      ),
      exportRender: (value) => String(value)
    },
    {
      key: 'position',
      header: 'Position',
      sortable: true,
      searchable: true,
      render: (value) => (
        <div className="text-center">
          <Badge variant="outline" className={`whitespace-normal ${getStatusColor('info', 'light')}`}>
            {String(value)}
          </Badge>
        </div>
      ),
      exportRender: (value) => String(value)
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      searchable: true,
      render: (value) => (
        <div className="text-center">
          <StatusBadge 
            status={String(value)}
            className="whitespace-normal"
          />
        </div>
      ),
      exportRender: (value) => String(value)
    },
    {
      key: 'residency',
      header: 'Residency',
      sortable: true,
      searchable: true,
      render: (value) => {
        const isResident = String(value).toLowerCase().includes('residen') && 
                          !String(value).toLowerCase().includes('non');
        return (
          <div className="text-center">
            <Badge 
              variant="outline" 
              className={isResident 
                ? 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/20 dark:text-blue-300 dark:border-blue-800' 
                : 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/20 dark:text-amber-300 dark:border-amber-800'
              }
            >
              {isResident ? 'Residence' : 'Non-Residence'}
            </Badge>
          </div>
        );
      },
      exportRender: (value) => String(value)
    },
    {
      key: 'group_name',
      header: 'Group',
      sortable: true,
      searchable: true,
      render: (value) => (
        <div className="text-center">
          {value ? (
            <Badge variant="default" className="bg-purple-100 text-purple-800 border-purple-200 dark:bg-purple-950/20 dark:text-purple-300 dark:border-purple-800">
              <Users className="h-3 w-3 mr-1" />
              {String(value)}
            </Badge>
          ) : (
            <span className="text-sm text-muted-foreground">No Group</span>
          )}
        </div>
      ),
      exportRender: (value) => String(value || 'No Group')
    },
    {
      key: 'actions' as unknown as keyof Row,
      header: 'Actions',
      sortable: false,
      searchable: false,
      className: 'w-32',
      render: (_value, row) => (
        <div className="text-center">
          <Button
            size="sm"
            variant="secondary"
            className="h-8 px-3 text-xs"
            onClick={() => handleIndividualEmployeeAssignment(row.id)}
          >
            Assign Group
          </Button>
        </div>
      ),
      exportRender: () => ''
    },
  ], [selectedEmployees, toggleEmployeeSelection, allFilteredSelected, toggleSelectAllFiltered, handleIndividualEmployeeAssignment]);

  if (isLoading && dutyRosterEmployees.length === 0) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
      </div>
    );
  }
  
  if (dutyRosterEmployees.length === 0 && !isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <FileQuestion className="h-16 w-16 text-muted-foreground mb-4" />
        <h3 className="text-xl font-semibold mb-2">No duty roster employees found</h3>
        <p className="text-muted-foreground mb-6 max-w-md">
          You haven't added any duty roster employees yet. 
          Create employees with duty roster enabled to manage them here.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-lg border bg-card">
      <DataTable
        data={tableRows}
        columns={columns}
        title="System Employees"
        isLoading={isLoadingDutyRosterEmployees}
        config={{
          searchPlaceholder: 'Search by name, employee ID, department, or position...',
          defaultSortField: 'name',
          rowsPerPage: 10,
          showSearch: true,
          showExport: false,
          showColumnVisibility: true,
          showRefresh: false,
          emptyStateMessage: 'No employees found'
        }}
        toolbarChildren={
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Department</span>
              <Popover open={deptFilterOpen} onOpenChange={setDeptFilterOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={deptFilterOpen}
                    className="w-48 justify-between"
                  >
                    {deptFilter === '' ? "All departments" : deptFilter}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-48 p-0">
                  <Command>
                    <CommandInput placeholder="Search departments..." />
                    <CommandList>
                      <CommandEmpty>No department found.</CommandEmpty>
                      <CommandGroup>
                        <CommandItem
                          onSelect={() => {
                            setDeptFilter('');
                            setDeptFilterOpen(false);
                          }}
                          className="flex items-center justify-between"
                        >
                          <div className="flex items-center">
                            <Check
                              className={cn(
                                "mr-2 h-4 w-4",
                                deptFilter === '' ? "opacity-100" : "opacity-0"
                              )}
                            />
                            <span className="font-medium">All departments</span>
                          </div>
                        </CommandItem>
                        {Array.from(new Set(dutyRosterEmployees.map(e => e.department || 'Unassigned'))).sort().map((dep: string) => (
                          <CommandItem
                            key={String(dep)}
                            onSelect={() => {
                              setDeptFilter(String(dep) === 'Unassigned' ? '' : String(dep));
                              setDeptFilterOpen(false);
                            }}
                            className="flex items-center justify-between"
                          >
                            <div className="flex items-center">
                              <Check
                                className={cn(
                                  "mr-2 h-4 w-4",
                                  deptFilter === (String(dep) === 'Unassigned' ? '' : String(dep)) ? "opacity-100" : "opacity-0"
                                )}
                              />
                              <span className="font-medium">{String(dep)}</span>
                            </div>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Group</span>
              <Popover open={groupFilterOpen} onOpenChange={setGroupFilterOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={groupFilterOpen}
                    className="w-48 justify-between"
                  >
                    {groupFilter.length === 0
                      ? "All groups"
                      : groupFilter.length === 1
                      ? dutyRosterGroups?.find(g => g.id === groupFilter[0])?.group_name || "Selected"
                      : `${groupFilter.length} groups selected`}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-48 p-0">
                  <Command>
                    <CommandInput placeholder="Search groups..." />
                    <CommandList>
                      <CommandEmpty>No group found.</CommandEmpty>
                      <CommandGroup>
                        {dutyRosterGroups?.map((group) => (
                          <CommandItem
                            key={group.id}
                            onSelect={() => {
                              setGroupFilter(current => {
                                const isSelected = current.includes(group.id);
                                if (isSelected) {
                                  return current.filter(id => id !== group.id);
                                } else {
                                  return [...current, group.id];
                                }
                              });
                            }}
                            className="flex items-center justify-between"
                          >
                            <div className="flex items-center">
                              <Check
                                className={cn(
                                  "mr-2 h-4 w-4",
                                  groupFilter.includes(group.id) ? "opacity-100" : "opacity-0"
                                )}
                              />
                              <span className="font-medium">{group.group_name}</span>
                            </div>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
          </div>
        }
      />

      {/* Floating Action Bar */}
      {selectedEmployeeIds.length > 0 && (
        <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 z-50">
          <div className="bg-white rounded-xl shadow-lg border border-gray-200 px-4 py-2 flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Checkbox
                checked={selectedEmployeeIds.length > 0}
                className="h-4 w-4"
              />
              <span className="text-gray-700 font-medium text-sm">
                {selectedEmployeeIds.length === 0 ? 'No employees selected' : `${selectedEmployeeIds.length} employee${selectedEmployeeIds.length !== 1 ? 's' : ''} selected`}
              </span>
            </div>

            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                className={`flex items-center gap-2 hover:bg-gray-100 ${getStatusColor('neutral', 'light')}`}
                onClick={() => setIsAssignDialogOpen(true)}
                disabled={selectedEmployeeIds.length === 0 || isBulkAssigning}
              >
                <Users className="h-4 w-4" />
                Assign Group
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Assign Group Dialog */}
      <AlertDialog open={isAssignDialogOpen} onOpenChange={setIsAssignDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Assign Group to Employees</AlertDialogTitle>
            {/* Residency matching warning */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-4 dark:bg-blue-950/20 dark:border-blue-800">
              <div className="flex items-center">
                <svg className="h-4 w-4 text-blue-400 dark:text-blue-300 mr-2" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
                <p className="text-sm text-blue-700 dark:text-blue-300">
                  <strong>Residency must match:</strong> Residence employees → Residence groups, Non-Residence employees → Non-Residence groups
                </p>
              </div>
            </div>
          </AlertDialogHeader>
          
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Select Group</label>
              <select
                value={selectedGroupId}
                onChange={(e) => setSelectedGroupId(e.target.value)}
                className="w-full mt-1 p-2 border border-gray-300 rounded-md"
                disabled={isLoadingGroups}
              >
                <option value="">Choose a group...</option>
                {getFilteredGroups().map(group => (
                  <option key={group.id} value={group.id}>
                    {group.group_name} ({group.residency_type})
                  </option>
                ))}
              </select>
              {selectedEmployeeResidencies.length > 0 && getFilteredGroups().length === 0 && (
                <p className="text-sm text-red-600 mt-2">
                </p>
              )}
            </div>
          </div>

          <AlertDialogFooter>
            <div className="flex items-center justify-between w-full">
              {selectedEmployeeResidencies.length > 1 && getFilteredGroups().length === 0 && (
                <p className="text-sm text-red-600">
                  Can't assign employees of different residency types to same group.
                </p>
              )}
              <div className="flex space-x-2 ml-auto">
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction 
                  onClick={handleAssignGroup}
                  disabled={!selectedGroupId || isBulkAssigning}
                >
                  {isBulkAssigning ? 'Assigning...' : 'Assign Group'}
                </AlertDialogAction>
              </div>
            </div>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
